import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SalesDetailPage from './SalesDetailPage';
import { W9_TRACKING_COLUMNS, SORT_TYPES, getDefaultVisibleColumns, getDefaultHiddenColumns } from '../constants/w9TrackingColumnsConfig';
import { buildW9TrackingFilterGroups } from '../constants/w9TrackingFiltersConfig';

const YES_BADGE = { bg: 'var(--sl-bg-success-primary, #ecfdf3)', color: 'var(--sl-text-success, #027a48)' };
const NO_BADGE = { bg: 'var(--sl-bg-warning-primary, #fff4e5)', color: 'var(--sl-text-warning, #b54708)' };

const buildDetailApiFilters = (activeFilters) => {
  const params = {};
  if (activeFilters.w9_status && activeFilters.w9_status.length > 0) {
    params.w9_status = activeFilters.w9_status[0];
  }
  if (activeFilters.venue_ownership_id && activeFilters.venue_ownership_id.length > 0) {
    params.venue_ownership_id = activeFilters.venue_ownership_id;
  }
  return params;
};

const W9_TRACKING_CONFIG = {
  pageTitle: 'W-9 Tracking',
  pageSubtitle: 'Confirmed events and whether a W-9 is on file.',
  sidebarKey: 'finances_w9_tracking',
  defaultPreset: 'this_year',
  dateModeOptions: [{ key: 'event_date', label: 'Event Date' }],
  defaultSortRules: [{ field: 'event_date', direction: 'desc' }],

  columns: W9_TRACKING_COLUMNS,
  sortTypes: SORT_TYPES,
  getDefaultVisibleColumns,
  getDefaultHiddenColumns,
  localStorageKey: (teamId) => `reportingFinancesW9Tracking_${teamId}`,
  firstColumnId: 'event_name',

  kpiFromDetailResponse: true,
  salesDataKey: null,
  venueSeriesKey: null,
  detailEndpoint: (teamId) => `/teams/${teamId}/reporting/finances/w9-tracking/data.json`,
  detailResponseKey: 'rows',
  exportBasePath: (teamId) => `/teams/${teamId}/reporting/finances/w9-tracking/export`,
  detailErrorMessage: 'Failed to load W-9 tracking data.',
  detailTableTitle: 'Confirmed Events',
  emptyMessage: 'No confirmed events in this range.',
  searchPlaceholder: 'Search events...',
  paginationLabel: 'events',
  itemLabel: (count) => `event${count !== 1 ? 's' : ''}`,

  buildDetailApiFilters,

  buildKpiCards: (current) => [
    { label: 'Confirmed Events', value: current.total_events || 0 },
    { label: 'With W-9', value: current.with_w9 || 0 },
    { label: 'Missing W-9', value: current.missing_w9 || 0 },
    { label: 'Coverage', value: `${current.coverage_pct || 0}%` },
  ],
  kpiGridColumns: 4,

  overTimeKey: null,
  topCharts: [],

  renderCellValue: (col, row, { formatEventDate }) => {
    switch (col.id) {
      case 'event_name': return row.event_name || '—';
      case 'event_date': return <span className="text-nowrap">{row.event_date ? formatEventDate(row.event_date) : '—'}</span>;
      case 'venue_name': return row.venue_name || '—';
      case 'has_w9': {
        const badge = row.has_w9 ? YES_BADGE : NO_BADGE;
        return <span className="badge" style={{ background: badge.bg, color: badge.color, fontSize: 12 }}>{row.has_w9 ? 'Yes' : 'No'}</span>;
      }
      case 'w9_count': return row.w9_count ?? 0;
      default: return '—';
    }
  },

  filterTitle: 'Filter Events',
  filterSubtitle: 'Apply filters to narrow which confirmed events are shown.',
};

function ReportingFinancesW9TrackingPage({ team, csrfToken, eventPageLayout, ...props }) {
  const [filterOptions, setFilterOptions] = useState({});

  // Link to the event page the user has chosen. The confirms controller cross-redirects
  // either way, but #assets only works on the tabbed page, and classic has no assets anchor.
  const eventHref = (eventId) => (
    eventPageLayout === 'tabbed'
      ? `/teams/${team.id}/e/${eventId}#assets`
      : `/teams/${team.id}/confirms/${eventId}`
  );

  useEffect(() => {
    if (!team?.id) return;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    axios.get(`/teams/${team.id}/reporting/finances/w9-tracking/filter_options.json`)
      .then((res) => setFilterOptions(res.data || {}))
      .catch((error) => console.error('Failed to load W-9 tracking filter options', error));
  }, [team?.id, csrfToken]);

  const configWithFilters = {
    ...W9_TRACKING_CONFIG,
    buildFilterGroups: () => buildW9TrackingFilterGroups(filterOptions),
    urlFilterIds: ['w9_status'],
    renderCellValue: (col, row, formatters) => {
      if (col.id === 'event_name') {
        if (!row.event_name) return '—';
        return team?.id && row.id
          ? <a href={eventHref(row.id)}>{row.event_name}</a>
          : row.event_name;
      }
      return W9_TRACKING_CONFIG.renderCellValue(col, row, formatters);
    },
  };
  return <SalesDetailPage {...props} team={team} csrfToken={csrfToken} config={configWithFilters} />;
}

export default ReportingFinancesW9TrackingPage;
